// src/components/general/form/textAreaFiledV2T.tsx

import { useEffect, useRef } from "react";
import { Control, FieldErrors, Path } from "react-hook-form";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import Icon from "../icon";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Noti from "../noti";
import { corrections } from "../../../../constants/constant/correctionVoiceToText";

type TextAreaFieldV2TProps<T extends Record<string, any>> = {
  name: Path<T>;
  value?: string;
  onChange: (newValue: string) => void;
  control: Control<T>;
  placeholder?: string;
  className?: string;
  rows?: number;
  cols?: number;
  disabled?: boolean;
  error?: FieldErrors<T>;
  activeSpeechField: string | null;
  setActiveSpeechField: (name: string | null) => void;
};

const TextAreaFieldV2T = <T extends Record<string, any>>({
  name,
  value,
  onChange,
  control,
  placeholder = "متن تبدیل شده از صوت",
  className = "",
  rows = 4,
  cols,
  disabled = false,
  error,
  activeSpeechField,
  setActiveSpeechField,
}: TextAreaFieldV2TProps<T>) => {
  const { t } = useTranslation();

  const {
    transcript: currentTranscript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable,
  } = useSpeechRecognition();

  // --- REFS ---
  // Ref to track the timestamp of the last transcript update.
  const lastActivityTimeRef = useRef<number>(Date.now());
  // Ref for the inactivity check timer.
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to store the text before recording started.
  const textBeforeRecordingRef = useRef<string>("");

  // Process text for Persian to English corrections (remains the same)
  const processTranscript = (text: string) => {
    // ... implementation is correct ...
    const normalizedText = text
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/[ـ]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    let processedText = ` ${normalizedText} `;
    Object.entries(corrections).forEach(([persian, english]) => {
      const regex = new RegExp(` ${persian} `, "gi");
      processedText = processedText.replace(regex, ` ${english} `);
    });
    return processedText.trim();
  };

  // Handler for manual typing (remains the same)
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  // ✅ STEP 1: Effect to process the transcript and update the activity timestamp.
  useEffect(() => {
    if (!listening || activeSpeechField !== name) return;

    // Any change to the transcript (even interim) is considered activity.
    // Update the timestamp.
    lastActivityTimeRef.current = Date.now();

    const processedNewText = processTranscript(currentTranscript);
    const newFullText = textBeforeRecordingRef.current
      ? `${textBeforeRecordingRef.current} ${processedNewText}`
      : processedNewText;

    if (newFullText.trim() !== (value || "").trim()) {
      onChange(newFullText);
    }
  }, [currentTranscript, listening, name, activeSpeechField]); // This effect ONLY handles transcript changes.

  // ✅ STEP 2: Effect to manage the "inactivity watchdog" timer.
  useEffect(() => {
    // If this field is the one listening...
    if (listening && activeSpeechField === name) {
      // Start a "watchdog" timer that checks for inactivity every second.
      inactivityTimerRef.current = setInterval(() => {
        const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;

        // If it's been more than 5 seconds since the last transcript change...
        if (timeSinceLastActivity > 5000) {
          console.log("Stopping due to 5 seconds of inactivity.");
          SpeechRecognition.stopListening();
          setActiveSpeechField(null); // This will cause this effect to clean up.
        }
      }, 1000); // Check every second.
    }

    // Cleanup function: This runs when listening stops OR the component unmounts.
    return () => {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [listening, name, activeSpeechField, setActiveSpeechField]); // This effect ONLY handles the timer logic.

  // Handler for the microphone button
  const toggleListening = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (activeSpeechField === name) {
      SpeechRecognition.stopListening();
      setActiveSpeechField(null);
    } else {
      SpeechRecognition.stopListening();

      // Reset activity timer when starting a new session.
      lastActivityTimeRef.current = Date.now();
      textBeforeRecordingRef.current = value?.trim() || "";
      resetTranscript();
      setActiveSpeechField(name as string);
      SpeechRecognition.startListening({
        language: "fa-IR",
        continuous: true,
        interimResults: true,
      }).catch((error) => {
        console.error("Speech recognition start error:", error);
      });
    }
  };

  // Handler for the reset button
  const handleReset = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    resetTranscript();
    onChange("");
  };

  // Browser support checks (remain the same)
  if (!browserSupportsSpeechRecognition) {
    Noti("error", "مرورگر شما قابلیت تبدیل صوت به متن را ندارد");
  }
  if (!isMicrophoneAvailable) {
    Noti("error", "میکروفون یافت نشد. لطفا مجوزهای لازم را بررسی نمایید.");
  }

  const isListeningForThisField = listening && activeSpeechField === name;

  return (
    // ... JSX remains exactly the same ...
    <div className="speech-textarea-wrapper form-group mb-4" dir="rtl">
      <textarea
        placeholder={placeholder}
        className={classNames("form-control", className, {
          "is-invalid": !!error?.[name],
        })}
        rows={rows}
        cols={cols}
        disabled={disabled}
        value={value || ""}
        onChange={handleTextAreaChange}
      />
      {error?.[name] && error[name]?.message && (
        <div className="invalid-feedback">
          {t(error[name]?.message as string)}
        </div>
      )}
      <div
        className="d-flex justify-content-between align-items-center mt-2"
        style={{ minHeight: "38px" }}
      >
        <div className="speech-status">
          {isListeningForThisField && (
            <div className="wave-container">
              <span className="wave-bar"></span>
              <span className="wave-bar"></span>
              <span className="wave-bar"></span>
              <span className="wave-bar"></span>
              <span className="wave-bar"></span>
            </div>
          )}
        </div>
        <div className="speech-controls d-flex">
          <button
            type="button"
            className={classNames("btn btn-sm p-2 border m-1", {
              "btn-green": isListeningForThisField,
              "btn-light-blue": !isListeningForThisField,
              disabled: disabled,
            })}
            onClick={toggleListening}
            disabled={disabled}
            title={t(isListeningForThisField ? "توقف" : "شروع ضبط")}
            aria-label={t(isListeningForThisField ? "توقف" : "شروع ضبط")}
          >
            <Icon name="microphone" />
          </button>
          <button
            type="button"
            className="btn btn-red p-2 ms-2 m-1"
            onClick={handleReset}
            disabled={disabled || !value || value.length === 0}
            title={"ریست متن"}
            aria-label={"ریست متن"}
          >
            <Icon name="trash" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextAreaFieldV2T;
